import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import {
  createFileRoute,
  redirect,
  Outlet,
  Link,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import {
  LayoutDashboard,
  User,
  MapPin,
  Package,
  FileText,
  Building2,
  Tag,
  ShieldCheck,
  Users,
  HelpCircle,
  Newspaper,
  Sparkles,
  Search,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,

} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  head: () => ({
    meta: [{ name: "robots", content: "noindex, nofollow" }],
  }),
  beforeLoad: async ({ location }) => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) {
      // Preserve the intended internal destination so /auth can bounce back.
      throw redirect({ to: "/auth", search: { redirect: location.href } });
    }
    return { user: data.user };
  },

  component: AuthLayout,
});


type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  exact?: boolean;
  children?: Omit<NavItem, "children">[];
};

const mainNav: NavItem[] = [
  { to: "/account", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/account/bestellingen", label: "Bestellingen", icon: Package },
  { to: "/account/offertes", label: "Offertes", icon: FileText },
  { to: "/prijzen", label: "Prijslijst", icon: Tag },
];

const accountNav: NavItem[] = [
  { to: "/account/profiel", label: "Profiel", icon: User },
  { to: "/account/adressen", label: "Adressen", icon: MapPin },
  { to: "/account/zakelijk", label: "Bedrijfsgegevens", icon: Building2 },
  { to: "/account/beveiliging", label: "Beveiliging", icon: ShieldCheck },
];

const adminNav: NavItem[] = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin/producten", label: "Producten", icon: Package },
  { to: "/admin/orders", label: "Bestellingen", icon: FileText },
  {
    to: "/admin/offertes",
    label: "Offertes",
    icon: FileText,
    children: [{ to: "/admin/facturen", label: "Facturen", icon: FileText }],
  },
  { to: "/admin/kanalen", label: "Verkoopkanalen", icon: Sparkles },
  { to: "/admin/klanten", label: "Klanten", icon: Users },
  { to: "/admin/faqs", label: "FAQ beheer", icon: HelpCircle },
  { to: "/admin/blog", label: "Blog beheer", icon: Newspaper },
  { to: "/admin/seo", label: "SEO controle", icon: Search },
];

function initials(name?: string | null, email?: string | null) {
  const base = (name ?? email ?? "?").trim();
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7dd3fc] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0e3463]";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';

function NavLink({
  item,
  onClick,
  nested,
  extraRightPadding,
}: {
  item: NavItem;
  onClick?: () => void;
  nested?: boolean;
  extraRightPadding?: boolean;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = item.exact
    ? pathname === item.to
    : pathname.startsWith(item.to);
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${focusRing} ${
        active
          ? "bg-white text-[#0b2a4a] shadow-sm"
          : "text-white/70 hover:bg-white/10 hover:text-white"
      } ${nested ? "pl-9" : ""} ${extraRightPadding ? "pr-10" : ""}`}
    >
      <Icon
        className={`size-4 shrink-0 ${active ? "text-[#3a7bd5]" : ""}`}
        aria-hidden="true"
      />
      <span className="flex-1 truncate">{item.label}</span>
      {active && (
        <ChevronRight
          className="size-3.5 text-[#3a7bd5]"
          aria-hidden="true"
        />
      )}
    </Link>
  );
}

function NavItemRow({
  item,
  onItemClick,
}: {
  item: NavItem;
  onItemClick?: () => void;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hasChildren = !!item.children && item.children.length > 0;
  const hasActiveChild = item.children?.some((c) =>
    c.exact ? pathname === c.to : pathname.startsWith(c.to),
  );
  const parentActive = item.exact
    ? pathname === item.to
    : pathname.startsWith(item.to);
  const [subOpen, setSubOpen] = useState(
    (hasActiveChild ?? false) || parentActive,
  );
  const submenuId = useId();
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const submenuRef = useRef<HTMLDivElement | null>(null);

  const focusFirstChild = () => {
    const link = submenuRef.current?.querySelector<HTMLAnchorElement>("a");
    link?.focus();
  };

  const onToggleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!hasChildren) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      if (!subOpen) setSubOpen(true);
      else focusFirstChild();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (subOpen) setSubOpen(false);
    } else if (e.key === "ArrowDown" && subOpen) {
      e.preventDefault();
      focusFirstChild();
    }
  };

  const onSubmenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const links = Array.from(
      submenuRef.current?.querySelectorAll<HTMLAnchorElement>("a") ?? [],
    );
    const currentIndex = links.indexOf(document.activeElement as HTMLAnchorElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      links[Math.min(currentIndex + 1, links.length - 1)]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (currentIndex <= 0) toggleRef.current?.focus();
      else links[currentIndex - 1]?.focus();
    } else if (e.key === "ArrowLeft" || e.key === "Escape") {
      e.preventDefault();
      setSubOpen(false);
      toggleRef.current?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      links[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      links[links.length - 1]?.focus();
    }
  };

  return (
    <div className="space-y-0.5">
      <div className="relative">
        <NavLink
          item={item}
          onClick={onItemClick}
          extraRightPadding={hasChildren}
        />
        {hasChildren && (
          <button
            ref={toggleRef}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSubOpen((v) => !v);
            }}
            onKeyDown={onToggleKeyDown}
            aria-expanded={subOpen}
            aria-controls={submenuId}
            aria-label={`${item.label} submenu ${subOpen ? "inklappen" : "uitklappen"}`}
            className={`absolute inset-y-0 right-1 my-auto flex size-7 items-center justify-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white ${focusRing}`}
          >
            <ChevronDown
              className={`size-3.5 transition-transform duration-200 ${subOpen ? "rotate-0" : "-rotate-90"}`}
              aria-hidden="true"
            />
          </button>
        )}
      </div>
      {hasChildren && (
        <div
          id={submenuId}
          ref={submenuRef}
          role="group"
          aria-label={`${item.label} submenu`}
          hidden={!subOpen}
          onKeyDown={onSubmenuKeyDown}
          className="space-y-0.5"
        >
          {item.children!.map((c) => (
            <NavLink key={c.to} item={c} onClick={onItemClick} nested />
          ))}
        </div>
      )}
    </div>
  );
}


function Section({
  title,
  items,
  sectionKey,
  activeSection,
  onToggle,
  onItemClick,
}: {
  title: string;
  items: NavItem[];
  sectionKey: string;
  activeSection: string | null;
  onToggle: (key: string) => void;
  onItemClick?: () => void;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hasActive = items.some((i) => {
    const parentActive = i.exact ? pathname === i.to : pathname.startsWith(i.to);
    const childActive = i.children?.some((c) =>
      c.exact ? pathname === c.to : pathname.startsWith(c.to),
    );
    return parentActive || !!childActive;
  });
  const open = activeSection === sectionKey || (activeSection === null && hasActive);
  const regionId = useId();
  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => onToggle(sectionKey)}
        className={`flex w-full items-center justify-between rounded-md px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white/80 hover:text-white transition ${focusRing}`}
        aria-expanded={open}
        aria-controls={regionId}
        aria-label={`${title} sectie ${open ? "inklappen" : "uitklappen"}`}
      >
        <span>{title}</span>
        <ChevronDown
          className={`size-3 transition-transform duration-200 ${open ? "rotate-0" : "-rotate-90"}`}
          aria-hidden="true"
        />
      </button>
      <div
        id={regionId}
        role="group"
        aria-label={title}
        hidden={!open}
        className="space-y-0.5"
      >
        {items.map((i) => (
          <NavItemRow key={i.to} item={i} onItemClick={onItemClick} />
        ))}
      </div>
    </div>
  );
}


function AuthLayout() {
  const { user, profile, isAdmin, signOut } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const drawerTitleId = useId();
  const out = async () => {
    await signOut();
    nav({ to: "/auth" });
  };
  const close = () => setMobileOpen(false);
  const toggleSection = (key: string) => {
    setActiveSection((prev) => (prev === key ? null : key));
  };

  // Focus management for the mobile drawer:
  //  - on open: move focus to the first focusable inside (drawer close button)
  //  - on close: return focus to the Menu trigger button
  // Also lock body scroll while the drawer is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const previouslyFocused =
      (document.activeElement as HTMLElement | null) ?? null;
    const drawer = drawerRef.current;
    const first = drawer?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    first?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
      // Prefer the tracked trigger; fall back to whatever had focus before.
      const target = menuTriggerRef.current ?? previouslyFocused;
      target?.focus?.();
    };
  }, [mobileOpen]);

  // Trap Tab / Shift+Tab inside the drawer and let Escape close it.
  const onDrawerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== "Tab") return;
    const drawer = drawerRef.current;
    if (!drawer) return;
    const focusables = Array.from(
      drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter(
      (el) =>
        !el.hasAttribute("disabled") &&
        el.getAttribute("aria-hidden") !== "true" &&
        // Skip elements inside a hidden submenu group.
        !el.closest("[hidden]"),
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && (active === first || !drawer.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const SidebarBody = (
    <div className="flex h-full flex-col gap-6 bg-gradient-to-b from-[#0b2a4a] via-[#0e3463] to-[#1a4a85] p-5 text-white">
      <div className="flex items-center justify-between">
        <Link to="/" onClick={close} className="flex items-center gap-2.5">
          <div className="flex size-9 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20 backdrop-blur">
            <Sparkles className="size-4 text-[#7dd3fc]" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold tracking-tight">Elvion Group Nederland</div>
            <div className="text-[10px] uppercase tracking-widest text-white/85">
              Klantenportaal
            </div>
          </div>
        </Link>
        <button
          onClick={close}
          className="rounded-lg p-1.5 text-white/70 hover:bg-white/10 lg:hidden"
          aria-label="Sluit menu"
        >
          <X className="size-5" />
        </button>
      </div>

      <div className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="flex size-11 items-center justify-center rounded-full bg-gradient-to-br from-[#3a7bd5] to-[#7dd3fc] text-sm font-bold text-white shadow-lg shadow-[#3a7bd5]/30">
            {initials(profile?.full_name, user?.email)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">
              {profile?.full_name ?? user?.email}
            </p>
            <p className="truncate text-xs text-white/85">{user?.email}</p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide">
            {profile?.customer_type === "business" ? "Zakelijk" : "Particulier"}
          </span>
          {profile?.verification_status === "approved" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-300">
              Geverifieerd
            </span>
          )}
          {profile?.verification_status === "pending" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-200">
              In behandeling
            </span>
          )}
          {profile && Number(profile.discount_percentage ?? 0) > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#7dd3fc]/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#7dd3fc]">
              {Number(profile.discount_percentage)}% korting
            </span>
          )}
          {isAdmin && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#3a7bd5]/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#7dd3fc]">
              Admin
            </span>
          )}
        </div>
      </div>

      <nav className="flex-1 space-y-3">
        <Section title="Overzicht" items={mainNav} sectionKey="overzicht" activeSection={activeSection} onToggle={toggleSection} onItemClick={close} />
        <Section title="Account" items={accountNav} sectionKey="account" activeSection={activeSection} onToggle={toggleSection} onItemClick={close} />
        {isAdmin && <Section title="Beheer" items={adminNav} sectionKey="beheer" activeSection={activeSection} onToggle={toggleSection} onItemClick={close} />}
      </nav>


      <div className="space-y-3">
        <div className="rounded-2xl bg-gradient-to-br from-[#3a7bd5]/25 to-[#7dd3fc]/10 p-4 ring-1 ring-white/10">
          <p className="text-xs font-semibold text-white">Hulp nodig?</p>
          <p className="mt-1 text-[11px] leading-relaxed text-white/60">
            Elvion Group Nederland helpt u graag persoonlijk verder bij vragen over bestellingen of producten.
          </p>
          <Link
            to="/klantenservice"
            onClick={close}
            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#7dd3fc] hover:underline"
          >
            Neem contact op <ChevronRight className="size-3" />
          </Link>
        </div>
        <button
          onClick={out}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-white/70 transition hover:bg-[#3a7bd5]/20 hover:text-white"
        >
          <LogOut className="size-4" />
          Uitloggen
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-[280px] z-40 overflow-y-auto">
        {SidebarBody}
      </aside>

      {/* Mobile top bar */}
      <div className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-white/95 px-4 backdrop-blur lg:hidden">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex size-8 items-center justify-center rounded-lg bg-[#0b2a4a] text-white">
            <Sparkles className="size-4" />
          </div>
          <span className="text-sm font-bold text-[#0b2a4a]">Klantenportaal</span>
        </Link>
        <Button
          ref={menuTriggerRef}
          variant="outline"
          size="sm"
          onClick={() => setMobileOpen(true)}
          className="gap-2"
          aria-haspopup="dialog"
          aria-expanded={mobileOpen}
          aria-controls={drawerTitleId}
        >
          <Menu className="size-4" />
          Menu
        </Button>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={close}
            aria-hidden="true"
          />
          <div
            ref={drawerRef}
            id={drawerTitleId}
            role="dialog"
            aria-modal="true"
            aria-label="Hoofdmenu"
            onKeyDown={onDrawerKeyDown}
            className="absolute inset-y-0 left-0 h-full w-[300px] max-w-[85vw] shadow-2xl overflow-y-auto focus:outline-none"
            tabIndex={-1}
          >
            {SidebarBody}
          </div>
        </div>
      )}

      <main className="min-w-0 flex-1 lg:ml-[280px]">
        {profile?.verification_status === "pending" && (
          <div className="border-b border-amber-200 bg-amber-50 px-6 py-2.5 text-center text-xs font-medium text-amber-900">
            Uw zakelijke account wordt nog gecontroleerd. Prijzen en bestellen worden zichtbaar zodra u bent goedgekeurd.
          </div>
        )}
        {profile?.verification_status === "rejected" && (
          <div className="border-b border-red-200 bg-red-50 px-6 py-2.5 text-center text-xs font-medium text-red-900">
            Uw account is helaas afgewezen. Neem contact met ons op.
          </div>
        )}
        <div className="w-full px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
